import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  TextField,
  Autocomplete,
  Paper,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import DeleteIcon from "@mui/icons-material/Delete";

import StandardTable, { TableColumn } from "../common/StandardTable";
import ConfirmDialog from "../common/ConfirmDialog";
import AppButton from "../buttons/AppButton";

import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  addPoItem,
  createPurchaseOrder,
} from "../../redux/actions/purchaseOrderActions";
import { purchaseOrderActions } from "../../redux/slices/purchaseOrder";
import { formatCurrency, formatPlainNumber } from "../../utils/appUtils";
import { InventoryItemResponseDto } from "../../shared/dtos/inventoryItem/InventoryItemResponseDto";
import { PurchaseOrderRequestDto } from "../../shared/dtos/purchaseOrder/PurchaseOrderRequestDto";
import { SupplierSummaryDto } from "../../shared/dtos/supplier/SupplierSummaryDto";

interface SupplierOption {
  id: string;
  label: string;
}

interface PoDesktopViewProps {
  items: InventoryItemResponseDto[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  pagination: { page: number; limit: number };
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

const PoDesktopView: React.FC<PoDesktopViewProps> = ({
  items,
  loading,
  error,
  onRetry,
  pagination,
  onPageChange,
  onLimitChange,
  searchTerm,
  setSearchTerm,
}) => {
  const dispatch = useAppDispatch();
  const {
    cartItems,
    cartSubtotal,
    selectedSupplier,
    loadingCreate,
    serverStatus,
  } = useAppSelector((state) => state.purchaseOrder);
  const { items: suppliers } = useAppSelector((state) => state.supplier);
  const supplier = selectedSupplier as unknown as SupplierSummaryDto | null;
  const [isClearDialogOpen, setIsClearDialogOpen] = useState<boolean>(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] =
    useState<boolean>(false);

  const supplierOptions = useMemo(
    () => suppliers.map((s) => ({ id: s.id, label: s.name })),
    [suppliers],
  );

  const columns: TableColumn<InventoryItemResponseDto>[] = useMemo(
    () => [
      {
        id: "name",
        label: "NAME",
        minWidth: 150,
        format: (_, row) => <b>{row.name}</b>,
      },
      { id: "itemCode", label: "ITEM CODE", minWidth: 100 },
      { id: "category", label: "CATEGORY", minWidth: 100 },
      {
        id: "unitCost",
        label: "COST",
        minWidth: 100,
        format: (_, row) => (
          <Typography color="info.main" sx={{ fontWeight: "bold" }}>
            {formatPlainNumber(row.unitCost)}
          </Typography>
        ),
      },
    ],
    [],
  );

  const handleUpdateField = (
    inventoryItemId: string,
    field: "quantity" | "unitCost",
    value: string | number,
  ): void => {
    if (value === "" || Number(value) >= 0) {
      dispatch(
        purchaseOrderActions.poItemUpdateFields({
          inventoryItemId,
          updates: { [field]: value },
        }),
      );
    }
  };

  const handleCreatePO = (): void => {
    if (!supplier) return;
    const payload: PurchaseOrderRequestDto = {
      supplierId: supplier.id,
      orderDate: new Date().toISOString().split("T")[0],
      items: cartItems.map((i) => ({
        inventoryItemId: i.inventoryItemId,
        quantity: Number(i.quantity),
        unitCost: Number(i.unitCost),
      })),
    };
    dispatch(createPurchaseOrder(payload));
  };

  useEffect(() => {
    if (serverStatus?.operation === "create" && serverStatus?.status === 201) {
      setIsConfirmDialogOpen(false);
      setIsClearDialogOpen(false);
    }
  }, [serverStatus]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "3fr 1fr",
        gap: 2,
        height: "calc(100vh - 120px)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            New Purchase Order
          </Typography>
          <TextField
            size="small"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 300 }}
          />
        </Stack>
        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
          {error ? (
            <Box sx={{ textAlign: "center", mt: 5 }}>
              <Typography color="error">{error}</Typography>
              <AppButton
                variant="contained"
                color="error"
                onClick={onRetry}
                sx={{ mt: 2 }}
                pill
              >
                Retry
              </AppButton>
            </Box>
          ) : (
            <StandardTable
              columns={columns}
              data={items}
              loading={loading}
              page={pagination.page}
              count={0}
              rowsPerPage={pagination.limit}
              onPageChange={onPageChange}
              onRowsPerPageChange={onLimitChange}
              rowAction={(row) => (
                <IconButton
                  size="small"
                  color="success"
                  onClick={() =>
                    dispatch(
                      addPoItem({
                        id: row.id,
                        name: row.name,
                        unitCost: row.unitCost,
                        category: row.category || "N/A",
                      }),
                    )
                  }
                >
                  <AddCircleIcon />
                </IconButton>
              )}
            />
          )}
        </Box>
      </Box>

      {/* RIGHT: CART PANE */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "90%",
          overflow: "hidden",
          py: 1.5,
          px: 1,
          bgcolor: "background.paper",
          borderRadius: 2,
        }}
      >
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          <Autocomplete
            options={supplierOptions}
            getOptionLabel={(opt: SupplierOption) => opt.label || ""}
            value={
              supplierOptions.find((o) => o.id === selectedSupplier?.id) || null
            }
            onChange={(_, val: SupplierOption | null) => {
              const fullSupplier =
                suppliers.find((s) => s.id === val?.id) ?? null;
              dispatch(purchaseOrderActions.setPoSupplier(fullSupplier));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Supplier"
                variant="outlined"
                size="small"
              />
            )}
            sx={{ mb: 2 }}
          />
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Order Items ({cartItems.length})
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: "auto", pr: 0.5, minHeight: 0 }}>
          <Stack spacing={1}>
            {cartItems.length === 0 ? (
              <Typography
                variant="body2"
                color="text.disabled"
                align="center"
                sx={{ mt: 4 }}
              >
                Order is empty. Add products to start.
              </Typography>
            ) : (
              cartItems.map((cItem) => (
                <Paper
                  key={cItem.inventoryItemId}
                  variant="outlined"
                  sx={{ p: 1.5, bgcolor: "background.default" }}
                >
                  <Stack direction="column" spacing={1}>
                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", pr: 1, mb: 0.5 }}
                      >
                        {cItem.name}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          dispatch(
                            purchaseOrderActions.poItemRemove(
                              cItem.inventoryItemId,
                            ),
                          )
                        }
                        sx={{ mt: -0.5, mr: -0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                      >
                        {cItem.category || ""}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "bold",
                          color: "success.main",
                          whiteSpace: "nowrap",
                          ml: 1,
                        }}
                      >
                        {formatCurrency(
                          Number(cItem.unitCost) * Number(cItem.quantity),
                        )}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      sx={{ alignItems: "center" }}
                    >
                      <TextField
                        label="Unit Cost"
                        size="small"
                        type="number"
                        value={cItem.unitCost}
                        onChange={(e) =>
                          handleUpdateField(
                            cItem.inventoryItemId,
                            "unitCost",
                            e.target.value,
                          )
                        }
                        sx={{ width: 120 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleUpdateField(
                            cItem.inventoryItemId,
                            "quantity",
                            Number(cItem.quantity) - 1,
                          )
                        }
                      >
                        <RemoveCircleIcon fontSize="small" />
                      </IconButton>
                      <TextField
                        size="small"
                        type="number"
                        value={cItem.quantity}
                        onChange={(e) =>
                          handleUpdateField(
                            cItem.inventoryItemId,
                            "quantity",
                            e.target.value,
                          )
                        }
                        sx={{ width: 120 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleUpdateField(
                            cItem.inventoryItemId,
                            "quantity",
                            Number(cItem.quantity) + 1,
                          )
                        }
                      >
                        <AddCircleIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Paper>
              ))
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            mt: 2,
            pt: 2,
            borderTop: "1px dashed",
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", mb: 2 }}
          >
            <Typography variant="h6">Order Total:</Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "info.main" }}
            >
              {formatPlainNumber(cartSubtotal)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <AppButton
              variant="outlined"
              color="error"
              fullWidth
              pill
              onClick={() => setIsClearDialogOpen(true)}
            >
              Clear
            </AppButton>
            <AppButton
              variant="contained"
              color="primary"
              fullWidth
              pill
              disabled={cartItems.length === 0 || !supplier}
              onClick={() => setIsConfirmDialogOpen(true)}
            >
              Submit PO
            </AppButton>
          </Stack>
        </Box>
      </Box>

      {isConfirmDialogOpen && supplier && (
        <ConfirmDialog
          open={isConfirmDialogOpen}
          onClose={() => setIsConfirmDialogOpen(false)}
          onConfirm={handleCreatePO}
          title="Confirm Order"
          description={`Confirm order of ${formatCurrency(cartSubtotal)} to ${supplier.name}?`}
          confirmText="Submit Order"
          loading={loadingCreate}
          severity="warning"
        />
      )}

      {isClearDialogOpen && (
        <ConfirmDialog
          open={isClearDialogOpen}
          onClose={() => setIsClearDialogOpen(false)}
          onConfirm={() => {
            dispatch(purchaseOrderActions.clearPoCart());
            setIsClearDialogOpen(false);
          }}
          title="Clear Draft Order"
          description="Remove all items and clear selected supplier?"
          confirmText="Clear All"
          severity="error"
        />
      )}
    </Box>
  );
};

export default PoDesktopView;
